import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const ManageSubscriptionButton = ({ userProfile, className = "", compact = false }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleManageSubscription = async (e) => {
    e?.stopPropagation();
    try {
      setLoading(true);
      setErrorMessage(null);

      // 1. Hämta aktiv session från Supabase
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error(t(language, 'portal_login_required'));
      }

      // 2. Bestäm API-endpoint baserat på miljö
      const apiUrl = window.location.hostname === 'localhost'
        ? 'https://science-monitor.vercel.app/api/create-portal-session'
        : '/api/create-portal-session';

      // 3. Anropa vår Vercel Serverless Function
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      let responseData = null;
      try {
        responseData = await response.json();
      } catch {
        const fallbackText = await response.text().catch(() => '');
        responseData = { error: fallbackText || t(language, 'portal_error', { status: response.status }) };
      }

      if (!response.ok) {
        const apiError = responseData?.error;
        const errText = apiError === 'portal_customer_not_found'
          || apiError === 'Ingen Stripe-kund hittades för detta konto.'
          ? t(language, 'portal_customer_not_found')
          : apiError || t(language, 'portal_error', { status: response.status });
        throw new Error(errText);
      }

      // 4. Omdirigera direkt till den färdigautentiserade Stripe-portalen
      if (responseData?.url) {
        window.location.href = responseData.url;
      } else {
        throw new Error(t(language, 'portal_no_url'));
      }

    } catch (err) {
      console.error('Fel vid anrop till prenumerationsportalen:', err);
      const msg = err.message || t(language, 'portal_error', { status: 'unknown' });
      setErrorMessage(msg);
      if (compact) {
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const isPaidUser = 
    userProfile?.status === 'basic' || 
    userProfile?.status === 'premium' || 
    userProfile?.subscription_tier === 'basic' || 
    userProfile?.subscription_tier === 'premium';

  if (!isPaidUser) {
    return null;
  }

  // --- KOMPAKT LÄGE FÖR MOBIL-HEADER ---
  if (compact) {
    return (
      <button
        onClick={handleManageSubscription}
        disabled={loading}
        className={className || "flex items-center justify-center p-1.5 text-slate-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-full border border-amber-200 transition-colors shadow-sm active:scale-95 disabled:opacity-50"}
        title={t(language, 'portal_btn_manage')}
        aria-label={t(language, 'portal_btn_manage')}
      >
        {loading ? (
          <span className="inline-block animate-spin text-xs">⏳</span>
        ) : (
          <span className="text-sm">💳</span>
        )}
      </button>
    );
  }

  // --- FULLSTÄNDIGT LÄGE FÖR DESKTOP SIDEBAR ---
  return (
    <div className="w-full mb-2">
      <button
        onClick={handleManageSubscription}
        disabled={loading}
        className={className || "w-full flex items-center p-3 text-slate-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-colors min-h-[44px] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"}
      >
        <span className="text-xl flex-shrink-0">💳</span>
        <span className="ml-3 text-sm truncate font-medium">
          {loading ? t(language, 'portal_opening') : t(language, 'portal_btn_manage')}
        </span>
      </button>

      {errorMessage && (
        <span className="block mt-1 px-1 text-xs text-rose-500 font-medium">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export { ManageSubscriptionButton };
export default ManageSubscriptionButton;